import { useState } from 'react';

import { useSnackbar } from 'hooks/useSnackbar';

const CHUNK_SIZE = 5 * 1024 * 1024;

export type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

type UseChunkedUploaderProps = {
  workflowInfo: { id: string; name: string };
  onChange: (newJson: string) => void;
};

export const useChunkedUploader = ({
  workflowInfo,
  onChange,
}: UseChunkedUploaderProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const resetState = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setStatus('idle');
    setError(null);
  };

  const showError = (message: string) => {
    setStatus('error');
    setError(message);
    enqueueSnackbar(`Upload failed: ${message}`, { variant: 'error' });
  };

  const showSuccess = () => {
    setStatus('success');
    enqueueSnackbar('Upload completed successfully', { variant: 'success' });
  };

  const readFileAsText = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsText(file);
    });

  const uploadChunks = async (file: File) => {
    try {
      setStatus('uploading');
      setUploadProgress(0);

      const initRes = await fetch('/api/request/v1/workflow/upload/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workflow_name: workflowInfo.name }),
      });

      if (!initRes.ok)
        return showError(`Failed to initiate upload (${initRes.status})`);

      const { session_id } = await initRes.json();
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

      for (let i = 0; i < totalChunks; i++) {
        const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const chunkUrl = `/api/request/v1/workflow/upload/chunk?session_id=${encodeURIComponent(
          session_id
        )}&part_number=${i + 1}`;

        const chunkRes = await fetch(chunkUrl, {
          method: 'PUT',
          body: chunk,
        });

        if (!chunkRes.ok) return showError(`Chunk ${i + 1} failed`);

        setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
      }

      const completeRes = await fetch(
        '/api/request/v1/workflow/upload/complete',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id,
            workflow_id: workflowInfo.id,
          }),
        }
      );

      if (!completeRes.ok)
        return showError(`Failed to complete upload (${completeRes.status})`);

      const json = await readFileAsText(file);
      onChange(json);
      showSuccess();
    } catch (err) {
      showError((err as Error).message);
    }
  };

  const handleFileChange = async (file: File) => {
    setSelectedFile(file);
    setUploadProgress(0);
    setError(null);
    await uploadChunks(file);
  };

  const handleRetry = () => {
    if (selectedFile) void uploadChunks(selectedFile);
  };

  return {
    selectedFile,
    uploadProgress,
    status,
    error,
    handleFileChange,
    handleRetry,
    handleClear: resetState,
  };
};
